class CatalogOrder extends HTMLElement {
  constructor() {
    super();
    this.rows = [];
    this.debounceTimeout = null;
    this.STORAGE_KEY = 'catalog_order_data';
    this.pendingConfirm = false;
    this.defaultAddToCartLabel = '';
  }

  get strings() {
    return window.themeVariables?.strings?.catalogOrder || {};
  }

  isVariantPurchasable(variant) {
    return variant.available && variant.aktiv !== 'Nein';
  }

  getAddToCartButton() {
    return this.querySelector('.add-to-cart');
  }

  getAddToCartButtonLabel() {
    const button = this.getAddToCartButton();
    if (!button) return '';
    return button.contentElement?.textContent.trim() || button.textContent.trim();
  }

  setAddToCartLabel(label) {
    const button = this.getAddToCartButton();
    if (!button?.contentElement) return;
    button.contentElement.textContent = label;
  }

  setAddToCartBusy(isBusy) {
    const button = this.getAddToCartButton();
    if (!button) return;
    button.removeAttribute('aria-busy');
    if (isBusy) {
      button.setAttribute('aria-busy', 'true');
    }
  }

  showNotice(message, type = 'info') {
    const notice = this.querySelector('[data-catalog-order-notice]');
    if (!notice) return;
    notice.textContent = message;
    notice.hidden = false;
    notice.classList.toggle('catalog-order__notice--warning', type === 'warning');
  }

  hideNotice() {
    const notice = this.querySelector('[data-catalog-order-notice]');
    if (!notice) return;
    notice.hidden = true;
    notice.textContent = '';
    notice.classList.remove('catalog-order__notice--warning');
  }

  resetPendingConfirm() {
    this.pendingConfirm = false;
    if (this.defaultAddToCartLabel) {
      this.setAddToCartLabel(this.defaultAddToCartLabel);
    }
    if (!this.hasUnpurchasableFilledRows()) {
      this.hideNotice();
    }
  }

  resetToInitialState() {
    this.rows.forEach((row) => row.remove());
    this.rows = [];
    this.pendingConfirm = false;
    this.hideNotice();
    sessionStorage.removeItem(this.STORAGE_KEY);
    if (this.defaultAddToCartLabel) {
      this.setAddToCartLabel(this.defaultAddToCartLabel);
    }
    this.initializeRows();
    this.updateGrandTotal();
  }

  getFilledRows() {
    return this.rows.filter((row) => row.classList.contains('filled'));
  }

  getRowVariant(row) {
    const option = row.querySelector('.variant-selector')?.selectedOptions[0];
    return option ? JSON.parse(option.dataset.variant) : null;
  }

  isRowPurchasable(row) {
    const variant = this.getRowVariant(row);
    return Boolean(variant && this.isVariantPurchasable(variant));
  }

  hasUnpurchasableFilledRows() {
    return this.getFilledRows().some((row) => !this.isRowPurchasable(row));
  }

  getPurchasableItems() {
    return this.getFilledRows()
      .filter((row) => this.isRowPurchasable(row))
      .map((row) => ({
        id: this.getRowVariant(row).id,
        quantity: parseInt(row.querySelector('.quantity input')?.value || 0, 10) || 0
      }))
      .filter((item) => item.quantity > 0);
  }

  getSoldOutHTML() {
    return `<span class="catalog-order__sold-out">${this.strings.soldOut || 'Sold out'}</span>`;
  }

  getQuantitySelectorHTML() {
    return `
      <quantity-selector class="quantity-selector">
        <button type="button" class="quantity-selector__button">
          <svg role="presentation" focusable="false" width="10" height="2" class="icon icon-minus" viewBox="0 0 10 2">
            <path d="M0 0H10V2H0V0Z" fill="currentColor"></path>
          </svg>
        </button>
        <input type="number" is="quantity-input" inputmode="numeric" class="quantity-selector__input" name="quantity" value="1" step="1" min="1" autocomplete="off">
        <button type="button" class="quantity-selector__button">
          <svg role="presentation" focusable="false" stroke-width="2" width="10" height="10" class="icon icon-plus" viewBox="0 0 12 12">
            <path d="M6 0V12" fill="none" stroke="currentColor"></path>
            <path d="M0 6L12 6" fill="none" stroke="currentColor"></path>
          </svg>
        </button>
      </quantity-selector>
    `;
  }

  setupQuantityListeners(row) {
    const desktopQuantityInput = row.querySelector('.quantity input');
    const mobileQuantityInput = row.querySelector('.mobile-quantity input');
    if (!desktopQuantityInput && !mobileQuantityInput) return;

    [desktopQuantityInput, mobileQuantityInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => {
        this.resetPendingConfirm();
        [desktopQuantityInput, mobileQuantityInput].forEach((i) => {
          if (i && i !== input) i.value = input.value;
        });
        this.calculateRowTotal(row);
        this.updateGrandTotal();
        this.saveToStorage();
      });
      input.addEventListener('change', () => {
        this.resetPendingConfirm();
        [desktopQuantityInput, mobileQuantityInput].forEach((i) => {
          if (i && i !== input) i.value = input.value;
        });
        this.calculateRowTotal(row);
        this.updateGrandTotal();
        this.saveToStorage();
      });
    });
  }

  updateRowPurchasability(row) {
    const variant = this.getRowVariant(row);
    if (!variant) return;

    const purchasable = this.isVariantPurchasable(variant);
    const articleContent = row.querySelector('.catalog-article-content');
    const mobileQuantity = row.querySelector('.mobile-quantity');
    const quantityCell = row.querySelector('.quantity');
    const priceHTML = `${this.buildPriceHTML(variant.price, variant.compare_at_price)}<span class="price-info">${this.strings.priceInfo}</span>`;

    row.classList.toggle('catalog-order__row--unavailable', !purchasable);
    row.dataset.price = purchasable ? variant.price : 0;
    row.dataset.compareAtPrice = purchasable ? (variant.compare_at_price || 0) : 0;

    row.querySelector('.price').innerHTML = priceHTML;
    const mobilePriceElement = articleContent?.querySelector('.mobile-price');
    if (mobilePriceElement) {
      mobilePriceElement.innerHTML = priceHTML;
    }

    if (purchasable) {
      if (!quantityCell.querySelector('.quantity-selector')) {
        quantityCell.innerHTML = this.getQuantitySelectorHTML();
      }
      const mobileValue = mobileQuantity?.querySelector('.mobile-value');
      if (mobileValue && !mobileValue.querySelector('.quantity-selector')) {
        mobileValue.innerHTML = `
          ${this.getQuantitySelectorHTML()}
          <button class="delete-btn"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" fill="none"><path fill="${window.themeVariables.settings.accentColor}" d="M2.171 11.731c-.323 0-.6-.115-.83-.345a1.133 1.133 0 0 1-.345-.828V1.555H.633a.356.356 0 0 1-.363-.364.354.354 0 0 1 .363-.363h2.544c0-.15.056-.28.167-.392a.537.537 0 0 1 .393-.167h3.242c.15 0 .28.055.392.167a.537.537 0 0 1 .167.392h2.544a.356.356 0 0 1 .364.364.354.354 0 0 1-.364.363H9.72v9.002c0 .323-.115.6-.345.83-.23.229-.507.344-.83.344H2.172Zm6.82-10.176H1.724v9.002c0 .13.042.238.126.322a.436.436 0 0 0 .322.126h6.374c.13 0 .237-.042.321-.126a.435.435 0 0 0 .126-.322V1.555ZM4.129 9.551c.103 0 .19-.035.26-.105a.349.349 0 0 0 .103-.259V3.372a.356.356 0 0 0-.364-.363.354.354 0 0 0-.363.363v5.815c0 .104.035.19.105.26.07.069.156.104.259.104Zm2.46 0a.35.35 0 0 0 .259-.105.353.353 0 0 0 .104-.259V3.372a.35.35 0 0 0-.105-.258.351.351 0 0 0-.259-.105.352.352 0 0 0-.259.105.349.349 0 0 0-.104.258v5.815a.356.356 0 0 0 .364.363Z"></path></svg></button>
        `;
        mobileValue.querySelector('button.delete-btn')?.addEventListener('click', () => {
          row.remove();
          this.rows = this.rows.filter((r) => r !== row);
          this.resetPendingConfirm();
          this.updateGrandTotal();
          this.saveToStorage();
        });
      }
      this.setupQuantityListeners(row);
    } else {
      quantityCell.innerHTML = this.getSoldOutHTML();
      const mobileValue = mobileQuantity?.querySelector('.mobile-value');
      if (mobileValue) {
        mobileValue.innerHTML = `
          ${this.getSoldOutHTML()}
          <button class="delete-btn"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" fill="none"><path fill="${window.themeVariables.settings.accentColor}" d="M2.171 11.731c-.323 0-.6-.115-.83-.345a1.133 1.133 0 0 1-.345-.828V1.555H.633a.356.356 0 0 1-.363-.364.354.354 0 0 1 .363-.363h2.544c0-.15.056-.28.167-.392a.537.537 0 0 1 .393-.167h3.242c.15 0 .28.055.392.167a.537.537 0 0 1 .167.392h2.544a.356.356 0 0 1 .364.364.354.354 0 0 1-.364.363H9.72v9.002c0 .323-.115.6-.345.83-.23.229-.507.344-.83.344H2.172Zm6.82-10.176H1.724v9.002c0 .13.042.238.126.322a.436.436 0 0 0 .322.126h6.374c.13 0 .237-.042.321-.126a.435.435 0 0 0 .126-.322V1.555ZM4.129 9.551c.103 0 .19-.035.26-.105a.349.349 0 0 0 .103-.259V3.372a.356.356 0 0 0-.364-.363.354.354 0 0 0-.363.363v5.815c0 .104.035.19.105.26.07.069.156.104.259.104Zm2.46 0a.35.35 0 0 0 .259-.105.353.353 0 0 0 .104-.259V3.372a.35.35 0 0 0-.105-.258.351.351 0 0 0-.259-.105.352.352 0 0 0-.259.105.349.349 0 0 0-.104.258v5.815a.356.356 0 0 0 .364.363Z"></path></svg></button>
        `;
        mobileValue.querySelector('button.delete-btn')?.addEventListener('click', () => {
          row.remove();
          this.rows = this.rows.filter((r) => r !== row);
          this.resetPendingConfirm();
          this.updateGrandTotal();
          this.saveToStorage();
        });
      }
    }

    this.calculateRowTotal(row);
  }

  connectedCallback() {
    this.initialized = false;
    this.render();
    this.init();
  }

  render() {
    this.innerHTML = `
      <table>
        <thead>
          <tr>
            <th class="md-max:hidden">${window.themeVariables.strings.catalogOrder.articleNumber}</th>
            <th>${window.themeVariables.strings.catalogOrder.article}</th>
            <th class="md-max:hidden">${window.themeVariables.strings.catalogOrder.size}</th>
            <th class="md-max:hidden">${window.themeVariables.strings.catalogOrder.quantity}</th>
            <th class="md-max:hidden">${window.themeVariables.strings.catalogOrder.price}</th>
            <th class="md-max:hidden">${window.themeVariables.strings.catalogOrder.total}</th>
            <th class="md-max:hidden"></th>
          </tr>
        </thead>
        <tbody id="rows-container"></tbody>
      </table>
      <div class="total-section">
        <div class="catalog-order__notice text-sm" data-catalog-order-notice hidden role="status"></div>
        <div class="total-price">${window.themeVariables.strings.catalogOrder.total}: <span id="grand-total">${formatMoney(0, window.themeVariables.settings.moneyWithCurrencyFormat)}</span></div>
        <button type="button" class="add-to-cart button button--xl" is="custom-button" disabled>${window.themeVariables.strings.catalogOrder.addToCart}</button>
      </div>
    `;
  }

  async init() {
    await this.restoreFromStorage();
    this.initializeRows();
    this.setupEventListeners();
    const addToCartBtn = this.getAddToCartButton();
    if (addToCartBtn) {
      this.defaultAddToCartLabel = this.getAddToCartButtonLabel();
    }
    this.updateGrandTotal();
    this.initialized = true;
  }

  initializeRows() {
    const rowsToAdd = Math.max(1, 5 - this.rows.length);
    for (let i = 0; i < rowsToAdd; i++) {
      this.addRow();
    }
  }

  addRow() {
    const container = this.querySelector('#rows-container');
    const row = document.createElement('tr');
    row.innerHTML = `
      <td class="md-max:hidden mobile-show-not-filled">
        <div class="suggestions-container">
          <input type="text" class="input article-number" placeholder="${window.themeVariables.strings.catalogOrder.searchPlaceholder}" autocomplete="off">
          <div class="suggestions-dropdown"></div>
        </div>
      </td>
      <td class="article-name">
        <div class="catalog-article-content"></div>
        <div class="md:hidden mobile-product-details">
          <div class="mobile-size-quantity">
            <div class="mobile-size"></div>
            <div class="mobile-quantity"></div>
          </div>
          <div class="mobile-row-total"></div>
        </div>
      </td>
      <td class="size md-max:hidden"></td>
      <td class="quantity md-max:hidden"></td>
      <td class="price md-max:hidden"></td>
      <td class="row-total md-max:hidden mobile-show-not-filled"><span class="price-container"><span class="price-value">${formatMoney(0, window.themeVariables.settings.moneyWithCurrencyFormat)}</span><span class="price-info">${window.themeVariables.strings.catalogOrder.priceInfo}</span></span></td>
      <td class="delete md-max:hidden"></td>
    `;
    container.appendChild(row);
    this.rows.push(row);
    this.setupRowEventListeners(row);

    return row;
  }

  checkRowsFilled() {
    if (this.initialized && this.rows.every(row => row.querySelector('.article-number').value)) {
      this.addRow();
    }
  }

  setupRowEventListeners(row) {
    const articleNumberInput = row.querySelector('.article-number');
    const suggestionsDropdown = row.querySelector('.suggestions-dropdown');

    articleNumberInput.addEventListener('input', () => {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.fetchSuggestions(articleNumberInput.value, suggestionsDropdown, row);
        this.saveToStorage();
      }, 300);
    });

    articleNumberInput.addEventListener('focus', () => {
      if (articleNumberInput.value) {
        this.fetchSuggestions(articleNumberInput.value, suggestionsDropdown, row);
      }
    });

    articleNumberInput.addEventListener('blur', () => {
      setTimeout(() => {
        suggestionsDropdown.classList.remove('visible');
      }, 200);
    });
  }

  async fetchSuggestions(query, dropdown, row) {
    if (!query) {
      dropdown.classList.remove('visible');
      return;
    }

    try {
      // Normalize numeric catalog numbers:
      // - Remove spaces
      // - If it's a sequence of digits with at least 6 characters, use only the first 6 as search query
      const trimmedQuery = query.trim();
      const digitsOnly = trimmedQuery.replace(/\s+/g, '');
      let searchQuery = trimmedQuery;

      if (/^\d+$/.test(digitsOnly) && digitsOnly.length >= 6) {
        searchQuery = digitsOnly.slice(0, 6);
      }

      const response = await fetch(`${window.Shopify.routes.root !== '/' ? window.Shopify.routes.root : ''}/search?q=${encodeURIComponent(searchQuery)}&type=product&view=catalog`);
      const data = await response.json();
      
      // Filter products that have variants matching the (possibly normalized) query
      const matchingProducts = data.products.filter(product => 
        product.article_number?.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (matchingProducts.length > 0) {
        dropdown.innerHTML = matchingProducts
          .map(product => {
            return `
              <div class="suggestion-item" data-product='${JSON.stringify(product)}'>
                <span class="suggestion-sku">${product.article_number}</span>
              </div>
            `;
          }).join('');

        dropdown.classList.add('visible');

        // Add click handlers for suggestions
        dropdown.querySelectorAll('.suggestion-item').forEach(item => {
          item.addEventListener('click', () => {
            const product = JSON.parse(item.dataset.product);
            const input = dropdown.previousElementSibling;
            input.value = product.article_number;
            this.displayProductInfo(product, row);
            dropdown.classList.remove('visible');
          });
        });

        // Check for exact match
        const exactMatch = matchingProducts.find(product => 
          product.article_number === searchQuery
        );

        if (exactMatch) {
          this.displayProductInfo(exactMatch, row);
          dropdown.classList.remove('visible');
        }
      } else {
        dropdown.classList.remove('visible');
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      dropdown.classList.remove('visible');
    }
  }

  buildPriceHTML(price, comparePrice) {
    if (comparePrice && comparePrice > price) {
      return `<span class="price-container"><span class="price-value text-on-sale">${formatMoney(price, window.themeVariables.settings.moneyWithCurrencyFormat)}</span><span class="line-through text-subdued">${formatMoney(comparePrice, window.themeVariables.settings.moneyWithCurrencyFormat)}</span></span>`;
    } else {
      return `<span class="price-container"><span class="price-value">${formatMoney(price, window.themeVariables.settings.moneyWithCurrencyFormat)}</span></span>`;
    }
  }

  displayProductInfo(product, row) {
    this.resetPendingConfirm();
    row.classList.add('filled');
    const sizeCell = row.querySelector('.size');
    const articleNameCell = row.querySelector('.article-name');
    const articleContent = articleNameCell.querySelector('.catalog-article-content');
    const mobileSize = articleNameCell.querySelector('.mobile-size');
    const mobileQuantity = articleNameCell.querySelector('.mobile-quantity');
    
    // Clear and populate variant selector for desktop
    sizeCell.innerHTML = '';
    const variantSelector = document.createElement('select');
    variantSelector.classList.add('variant-selector', 'select');
    const firstPurchasableVariant = product.variants.find((variant) => this.isVariantPurchasable(variant)) || product.variants[0];
    product.variants.forEach(variant => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      option.dataset.variant = JSON.stringify(variant);
      if (!this.isVariantPurchasable(variant)) {
        option.disabled = true;
      }
      
      variantSelector.appendChild(option);
    });
    variantSelector.value = firstPurchasableVariant.id;
    sizeCell.appendChild(variantSelector);

    // Create mobile size selector
    const mobileVariantSelector = document.createElement('select');
    mobileVariantSelector.classList.add('variant-selector', 'select', 'mobile-variant-selector');
    product.variants.forEach(variant => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      option.dataset.variant = JSON.stringify(variant);
      if (!this.isVariantPurchasable(variant)) {
        option.disabled = true;
      }
      
      mobileVariantSelector.appendChild(option);
    });
    mobileVariantSelector.value = firstPurchasableVariant.id;

    mobileSize.innerHTML = `
      <div class="mobile-label">${window.themeVariables.strings.catalogOrder.size}:</div>
      <div class="mobile-value"></div>
    `;
    const mobileValueContainer = mobileSize.querySelector('.mobile-value');
    mobileValueContainer.appendChild(mobileVariantSelector);
    
    [variantSelector, mobileVariantSelector].forEach(selector => {
      selector.addEventListener('change', () => {
        const selectedVariant = selector.selectedOptions[0];
        if (!selectedVariant?.value) return;

        [variantSelector, mobileVariantSelector].forEach((s) => {
          if (s !== selector) s.value = selectedVariant.value;
        });

        this.resetPendingConfirm();
        this.updateRowPurchasability(row);
        this.updateGrandTotal();
        this.saveToStorage();
      });
    });

    // Create product info layout for desktop
    articleContent.innerHTML = `
      <div class="catalog-product-info">
        ${product.featured_image ? `
          <div class="catalog-product-image">
            <img src="${product.featured_image}" alt="${product.title}">
          </div>
        ` : ''}
        <div class="catalog-product-details">
          <div class="catalog-product-vendor">${product.vendor || ''}</div>
          <div class="catalog-product-title">
            <a href="${product.url}">${product.title}</a>
          </div>
          <div class="md:hidden">
            <span class="mobile-value">${product.article_number}</span>
          </div>
          ${product.color || product.length ? `
            <div class="catalog-product-variant">
              ${product.color ? `
                <div class="catalog-product-variant-item">
                  <span class="catalog-product-variant-item-label">${window.themeVariables.strings.catalogOrder.color}:</span>
                  <span class="catalog-product-variant-item-value">${product.color}</span>
                </div>
              ` : ''}
              ${product.length ? `
                <div class="catalog-product-variant-item">
                  <span class="catalog-product-variant-item-label">${window.themeVariables.strings.catalogOrder.length}:</span>
                  <span class="catalog-product-variant-item-value">${product.length}</span>
                </div>
              ` : ''}
            </div>
          ` : ''}
          <div class="md:hidden mobile-price">
            ${this.buildPriceHTML(firstPurchasableVariant.price, firstPurchasableVariant.compare_at_price)}<span class="price-info">${window.themeVariables.strings.catalogOrder.priceInfo}</span>
          </div>
        </div>
      </div>
    `;
    
    row.querySelector('.quantity').innerHTML = '';
    mobileQuantity.innerHTML = `
      <div class="mobile-label">${window.themeVariables.strings.catalogOrder.quantity}:</div>
      <div class="mobile-value"></div>
    `;

    // Create mobile total and delete
    const mobileRowTotal = articleNameCell.querySelector('.mobile-row-total');
    mobileRowTotal.innerHTML = `
      <span class="price-value mobile-value">${formatMoney(0, window.themeVariables.settings.moneyWithCurrencyFormat)}</span><span class="price-info">${window.themeVariables.strings.catalogOrder.priceInfo}</span>
    `;

    // Add event listeners to both quantity inputs
    row.querySelector('.delete').innerHTML = `<button type="button" class="delete-btn"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" fill="none"><path fill="${window.themeVariables.settings.accentColor}" d="M2.171 11.731c-.323 0-.6-.115-.83-.345a1.133 1.133 0 0 1-.345-.828V1.555H.633a.356.356 0 0 1-.363-.364.354.354 0 0 1 .363-.363h2.544c0-.15.056-.28.167-.392a.537.537 0 0 1 .393-.167h3.242c.15 0 .28.055.392.167a.537.537 0 0 1 .167.392h2.544a.356.356 0 0 1 .364.364.354.354 0 0 1-.364.363H9.72v9.002c0 .323-.115.6-.345.83-.23.229-.507.344-.83.344H2.172Zm6.82-10.176H1.724v9.002c0 .13.042.238.126.322a.436.436 0 0 0 .322.126h6.374c.13 0 .237-.042.321-.126a.435.435 0 0 0 .126-.322V1.555ZM4.129 9.551c.103 0 .19-.035.26-.105a.349.349 0 0 0 .103-.259V3.372a.356.356 0 0 0-.364-.363.354.354 0 0 0-.363.363v5.815c0 .104.035.19.105.26.07.069.156.104.259.104Zm2.46 0a.35.35 0 0 0 .259-.105.353.353 0 0 0 .104-.259V3.372a.35.35 0 0 0-.105-.258.351.351 0 0 0-.259-.105.352.352 0 0 0-.259.105.349.349 0 0 0-.104.258v5.815a.356.356 0 0 0 .364.363Z"></path></svg></button>`;
    row.querySelector('.delete button.delete-btn').addEventListener('click', () => {
      row.remove();
      this.rows = this.rows.filter(r => r !== row);
      this.resetPendingConfirm();
      this.updateGrandTotal();
      this.saveToStorage();
    });

    this.updateRowPurchasability(row);
    this.checkRowsFilled();
    this.updateGrandTotal();
    this.saveToStorage();
  }

  calculateRowTotal(row) {
    const price = parseFloat(row.dataset.price) || 0;
    const compareAtPrice = parseFloat(row.dataset.compareAtPrice) || 0;
    const quantity = parseInt(row.querySelector('.quantity input')?.value || 0, 10) || 0;
    const total = price * quantity;
    const compareTotal = compareAtPrice > price ? compareAtPrice * quantity : 0;

    // Desktop row total
    const rowTotalCell = row.querySelector('.row-total');
    if (rowTotalCell) {
      rowTotalCell.innerHTML = `${this.buildPriceHTML(total, compareTotal)}<span class="price-info">${window.themeVariables.strings.catalogOrder.priceInfo}</span>`;
    }

    // Mobile row total
    const mobileTotal = row.querySelector('.mobile-row-total .mobile-value');
    if (mobileTotal) {
      mobileTotal.innerHTML = this.buildPriceHTML(total, compareTotal);
    }
  }

  updateGrandTotal() {
    const total = this.rows.reduce((sum, row) => {
      if (!this.isRowPurchasable(row)) return sum;
      const price = parseFloat(row.dataset.price) || 0;
      const quantity = parseInt(row.querySelector('.quantity input')?.value || 0, 10) || 0;
      return sum + (price * quantity);
    }, 0);

    this.querySelector('#grand-total').textContent = formatMoney(total, window.themeVariables.settings.moneyWithCurrencyFormat);

    const purchasableItems = this.getPurchasableItems();
    const canSubmit = purchasableItems.length > 0;
    const hasFilledRows = this.getFilledRows().length > 0;
    const hasUnpurchasableRows = this.hasUnpurchasableFilledRows();
    const addToCartBtn = this.getAddToCartButton();

    if (!canSubmit && hasFilledRows && !this.pendingConfirm) {
      this.showNotice(this.strings.noPurchasableVariants || 'No available sizes in your list.');
    } else if (!this.pendingConfirm) {
      this.hideNotice();
    }

    if (addToCartBtn) {
      addToCartBtn.disabled = !canSubmit;
      if (this.pendingConfirm && canSubmit && hasUnpurchasableRows) {
        this.setAddToCartLabel(this.strings.addAvailableToCart || this.defaultAddToCartLabel);
      } else if (this.defaultAddToCartLabel) {
        this.setAddToCartLabel(this.defaultAddToCartLabel);
      }
    }

    this.checkRowsFilled();
  }

  setupEventListeners() {
    const addToCartBtn = this.querySelector('.add-to-cart');
    addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
  }

  async handleAddToCart(evt) {
    evt.preventDefault();

    const items = this.getPurchasableItems();

    if (items.length === 0) {
      this.showNotice(this.strings.noPurchasableVariants || 'No available sizes in your list.');
      this.setAddToCartBusy(false);
      return;
    }

    if (this.hasUnpurchasableFilledRows() && !this.pendingConfirm) {
      this.pendingConfirm = true;
      const notice = [this.strings.unavailableLinesNotice, this.strings.confirmAddAvailable].filter(Boolean).join(' ');
      this.showNotice(notice, 'warning');
      this.updateGrandTotal();
      this.setAddToCartBusy(false);
      return;
    }

    this.pendingConfirm = false;
    this.hideNotice();
    this.setAddToCartBusy(true);

    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { 
      bubbles: true, 
      detail: { sections: sectionsToBundle } 
    }));

    const formData = new FormData();
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });
    formData.set("sections", sectionsToBundle.join(","));

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        body: formData,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();
      if (response.ok) {
        if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
          sessionStorage.removeItem(this.STORAGE_KEY);
          return window.location.href = `${Shopify.routes.root}cart`;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent["sections"] = responseJson["sections"];

        this.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
            cart: cartContent
          }
        }));

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent
          }
        }));

        this.resetToInitialState();
      } else {
        this.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"]
          }
        }));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: "An error occurred while adding items to cart"
        }
      }));
    } finally {
      this.setAddToCartBusy(false);
      this.updateGrandTotal();
    }
  }

  saveToStorage() {
    const orderData = this.rows.map(row => {
      if (!row.classList.contains('filled')) return null;
      const articleNumber = row.querySelector('.article-number').value;
      const variantSelector = row.querySelector('.variant-selector');
      const quantityInput = row.querySelector('.quantity input');
      const selectedVariant = variantSelector.selectedOptions[0];
      
      if (!articleNumber || !selectedVariant) return null;

      return {
        articleNumber,
        variantId: selectedVariant?.value,
        quantity: quantityInput?.value || 1
      };
    }).filter(Boolean);

    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(orderData));
  }

  async restoreFromStorage() {
    const savedData = sessionStorage.getItem(this.STORAGE_KEY);
    if (!savedData) return;

    const orderData = JSON.parse(savedData);
    
    // Clear existing rows
    this.rows.forEach(row => row.remove());
    this.rows = [];

    // Create new rows and populate with saved data
    for (const item of orderData) {
      const row = this.addRow();
      const articleNumberInput = row.querySelector('.article-number');
      articleNumberInput.value = item.articleNumber;
      
      if (item.articleNumber) {
        await this.fetchSuggestions(item.articleNumber, row.querySelector('.suggestions-dropdown'), row);
        
        // Wait for variant selector to be populated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const variantSelector = row.querySelector('.variant-selector');
        const mobileVariantSelector = row.querySelector('.mobile-variant-selector');
        if (item.variantId) {
          const option = variantSelector?.querySelector(`option[value="${item.variantId}"]`);
          const variant = option ? JSON.parse(option.dataset.variant) : null;
          if (variant && this.isVariantPurchasable(variant)) {
            variantSelector.value = item.variantId;
            if (mobileVariantSelector) {
              mobileVariantSelector.value = item.variantId;
            }
          }
          variantSelector.dispatchEvent(new Event('change'));
          const quantityInputs = row.querySelectorAll('.quantity input');
          if (quantityInputs.length > 0) {
            quantityInputs.forEach(input => {
              input.value = item.quantity;
            });
            this.calculateRowTotal(row);
          }
        }
      }
    }

    this.updateGrandTotal();
  }
}

customElements.define('catalog-order', CatalogOrder);